import { Typography } from '@mui/material';
import Avatar from 'further-ui/components/Avatar';
import BasicSelect from 'further-ui/components/BasicSelect';
import SearchField from 'further-ui/components/SearchField';
import Table, { type Column } from 'further-ui/components/Table';
import { Column as LayoutColumn, Row } from 'further-ui/components/Layout';
import PageFilters, { Filter } from 'components/new/PageFilters';
import {
  dateToLabelAltFormat,
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import { ComponentProps } from 'react';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { ApiResponses } from 'lib/trpcClient';

type CompanyRow = ApiResponses['lpCompany']['list']['results'][number] & {
  id: string;
};

type Pagination = NonNullable<ComponentProps<typeof Table>['pagination']>;

type Props = {
  rows: Array<CompanyRow>;
  isLoading: boolean;
  totalCount: number;
  pagination: Pagination;
  search: string;
  sortSelectOptions: Array<{ label: string; value: string }>;
  sortSelectValue: string;
  baseCurrency: CurrencyCode | undefined;
  onSearchChange: (search: string) => void;
  onSortChange: (value: string) => void;
};

const ConfirmedRevaluationsView: React.FC<Props> = ({
  rows,
  isLoading,
  totalCount,
  pagination,
  search,
  sortSelectOptions,
  sortSelectValue,
  baseCurrency,
  onSearchChange,
  onSortChange,
}) => {
  const columns: Array<Column<CompanyRow>> = [
    {
      label: 'Asset',
      key: 'name',
      render: (company) => (
        <Row centered spacing="xs">
          <Avatar
            src={company.companyLogo ?? undefined}
            text={company.name}
            size="lg"
            color="random"
          />
          <div>{company.name}</div>
        </Row>
      ),
    },
    {
      label: 'Hold value',
      key: 'currentHoldValueLcy',
      disableSorting: true,
      render: (company) =>
        numberToCurrencyString(company.currentHoldValueLcy, {
          currency: baseCurrency,
        }),
    },
    {
      label: 'Average unit price',
      key: 'currentNumberOfUnits',
      disableSorting: true,
      render: (company) => {
        if (!company.currentNumberOfUnits) return '--';
        return numberToCurrencyString(
          company.currentHoldValueLcy / company.currentNumberOfUnits,
          { currency: baseCurrency },
        );
      },
    },
    {
      label: 'Units held',
      key: 'totalNumberOfUnits',
      disableSorting: true,
      render: (company) => numberToDisplayString(company.currentNumberOfUnits),
    },
    {
      label: 'Revaluation date',
      key: 'latestRevaluationDate',
      disableSorting: true,
      render: (company) => dateToLabelAltFormat(company.latestRevaluationDate),
    },
  ];

  return (
    <LayoutColumn fullWidth spacing="md">
      <LayoutColumn spacing="lg">
        <LayoutColumn>
          <Typography variant="h5">Company valuations</Typography>
          <Typography variant="body2">
            Showing the latest revaluation for each company at the period end
            date.
          </Typography>
        </LayoutColumn>
        <PageFilters justify="space-between">
          <Filter>
            <SearchField
              name="search"
              placeholder="Search by company"
              value={search}
              fullWidth
              onSearch={onSearchChange}
              allowEmptyQuery
              autoSearchOnDebounce
            />
          </Filter>
          <Filter>
            <BasicSelect
              fullWidth
              value={sortSelectValue}
              onChange={onSortChange}
              options={sortSelectOptions}
              placeholder="Sort by"
            />
          </Filter>
        </PageFilters>
      </LayoutColumn>
      <Table
        columns={columns}
        rows={rows}
        isLoading={isLoading}
        rowsTotalCount={totalCount}
        pagination={pagination}
        emptyMessage="No companies found"
      />
    </LayoutColumn>
  );
};

export default ConfirmedRevaluationsView;
